/**
 * What to tell the user when the API has refused their session (#692).
 *
 * `authError` holds the gate's `X-Auth-Error` reason (`device_token_not_found`, `oauth_rejected`,
 * …) or `unconfirmed` for a 401 that never carried one. By the time this copy is shown, a silent
 * re-auth has already been tried and failed — every transient failure is retried and never
 * gets here — so the honest message is "sign in again", not "a configuration problem".
 */
export interface AuthErrorCopy {
	title: string;
	body: string;
}

const SIGN_IN_AGAIN: AuthErrorCopy = {
	title: 'Please sign in again',
	body: 'Your sign-in on this device has expired, and we couldn\'t renew it automatically.',
};

/**
 * @param authError the reason code (or a legacy free-text message from an older path)
 * @param email the signed-in address, named where it helps the user act
 */
export const describeAuthError = (authError: string | null, email?: string | null): AuthErrorCopy => {
	switch (authError) {
		case 'user_not_found':
			return {
				title: 'This account doesn\'t have access yet',
				body: `You're signed in as ${email || 'this account'}, but it hasn't been set up in Property Work Order. `
					+ 'Ask an administrator to add you, then sign in again.',
			};
		case 'demo_org':
			return {
				title: 'Demo accounts can\'t open live data',
				body: 'Sign in with your own work account to continue.',
			};
		default:
			return SIGN_IN_AGAIN;
	}
};
